import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import apiFetch from "../utils/apiFetch";

// Persisted "use fixed sidebar" preference. When enabled (desktop only), the
// School layout detaches the nav menu from the hamburger drawer and renders it
// as a permanent left sidebar.
//
// Source of truth is the staff record (persisted via /api/staff_preference so
// the choice follows the user across devices), seeded into the provider through
// `initialEnabled` (from appProps) so first render is correct with no flash.
// localStorage is kept only as the pre-paint cache the inline script in the
// application layout reads before any React mounts; it is mirrored on every
// change and self-heals from the backend value on mount.
const STORAGE_KEY = "use_fixed_sidebar";

// Width of the permanent left sidebar the School layout renders when the
// preference is on. Shared so fixed-position chrome (e.g. the quick-actions
// FAB) can offset itself to stay aligned with the content area.
export const FIXED_SIDEBAR_WIDTH = 320;

function readFixedSidebar() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // localStorage can throw in private mode / blocked storage; default off.
    return false;
  }
}

function writeFixedSidebarCache(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

const FixedSidebarContext = createContext({
  enabled: false,
  setEnabled: () => {},
  toggle: () => {},
});

export function FixedSidebarProvider({ children, onChange, initialEnabled }) {
  // Seed from the backend-derived appProps value when present; fall back to the
  // localStorage cache otherwise. Synchronous initializer keeps first render
  // correct so the layout never flashes the wrong shell.
  const [enabled, setEnabledState] = useState(() =>
    typeof initialEnabled === "boolean" ? initialEnabled : readFixedSidebar()
  );

  // Reconcile the localStorage pre-paint cache with the backend value on mount.
  // On a device whose cache is stale (e.g. first visit after toggling elsewhere)
  // this writes the correct value so the next pre-paint read is right; the first
  // load may flash once, then self-heals.
  useEffect(() => {
    if (typeof initialEnabled !== "boolean") return;
    if (readFixedSidebar() !== initialEnabled) {
      writeFixedSidebarCache(initialEnabled);
    }
  }, [initialEnabled]);

  const setEnabled = useCallback(
    (next) => {
      setEnabledState(next);
      writeFixedSidebarCache(next);
      // Persist to the staff record so the choice follows the user across
      // devices. Errors are swallowed: local state already reflects the change.
      // onChange fires only after the PATCH settles - the legacy chrome reloads
      // in onChange (its navbar/sidebar/body are separate React roots that
      // cannot re-render off shared context), and reloading mid-request aborts
      // the fetch, leaving the backend value out of sync with the cache.
      apiFetch("/api/staff_preference", {
        method: "PATCH",
        body: JSON.stringify({ staff_preference: { use_fixed_sidebar: next } }),
      })
        .catch(() => {})
        .finally(() => onChange?.(next));
    },
    [onChange]
  );

  const toggle = useCallback(() => setEnabled(!enabled), [enabled, setEnabled]);

  const value = useMemo(
    () => ({ enabled, setEnabled, toggle }),
    [enabled, setEnabled, toggle]
  );

  return (
    <FixedSidebarContext.Provider value={value}>
      {children}
    </FixedSidebarContext.Provider>
  );
}

export function useFixedSidebar() {
  return useContext(FixedSidebarContext);
}
